import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Clapperboard, Download, Loader2, Play, Upload, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { NodeShell } from './NodeShell';
import { CircleActionButton, SvgRemove } from './ProductNode';
import { VideoComposition, formatTime, mainTrack, totalDuration } from '../video-editor/composition';
import type { VideoExportState } from '../video-editor/VideoEditorModal';
import type { StoryProgress } from '../video-editor/video-story';
import { useVideoPoster } from '../video-editor/media';

export const VIDEO_EDIT_NODE_WIDTH = 260;

interface VideoEditNodeProps {
    nodeId: string;
    x: number;
    y: number;
    zoom: number;
    selected?: boolean;
    dimmed?: boolean;
    composition: VideoComposition;
    renderedUrl?: string;
    renderedThumbnailUrl?: string;
    exportState?: VideoExportState | null;
    onOpenEditor: () => void;
    onExport: () => void;
    onCancelExport: () => void;
    onDrag: (dx: number, dy: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onRemove?: () => void;
    onDownload?: () => void;
    /** Set on a Video Story while it is being written, shot and animated. */
    story?: {
        progress: StoryProgress;
        title?: string;
        error?: string;
        /** Cut the timeline now from whatever has landed. */
        onAssembleNow?: () => void;
    } | null;
}

/**
 * A timeline on the canvas: its poster (or last export), length and shape,
 * and the two things to do with it — open the editor, or export.
 */
export const VideoEditNode: React.FC<VideoEditNodeProps> = ({
    nodeId,
    x,
    y,
    zoom,
    selected,
    dimmed,
    composition,
    renderedUrl,
    renderedThumbnailUrl,
    exportState,
    onOpenEditor,
    onExport,
    onCancelExport,
    onDrag,
    onClick,
    onRemove,
    onDownload,
    story,
}) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const clips = mainTrack(composition).clips;
    const duration = totalDuration(composition);
    const first = clips[0];
    const firstPoster = useVideoPoster(first?.src, first?.kind === 'image' ? first.src : first?.thumbnailUrl, first?.kind === 'video');
    const poster = renderedThumbnailUrl || firstPoster;
    const running = exportState?.status === 'running';
    const storyBusy = !!story && story.progress.phase !== 'ready';
    const storyStills = story
        ? story.progress.scenes.map((s) => s.video?.thumbnailUrl || s.image?.thumbnailUrl || s.image?.url).filter(Boolean) as string[]
        : [];
    const pct = Math.round((exportState?.progress?.progress ?? 0) * 100);
    // The card keeps one shape for every aspect so the canvas reads as a grid.
    const ratio = composition.width / composition.height;
    const previewH = Math.round(Math.min(300, Math.max(150, (VIDEO_EDIT_NODE_WIDTH - 2) / ratio)));

    return (
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={VIDEO_EDIT_NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent="#4A4945"
            dimmed={dimmed}
            label={story ? t('VIDEO_EDITOR.STORY.NODE_LABEL') : t('VIDEO_EDITOR.NODE.LABEL')}
            labelBadge={duration > 0 ? formatTime(duration) : undefined}
            labelBadgeTone="neutral"
            labelBadgeSecondary={composition.aspect}
            labelBadgeSecondaryTone="neutral"
            onDrag={onDrag}
            onClick={onClick}
            pulse={running || (storyBusy && story?.progress.phase !== 'failed')}
            bottomAffordance={(
                <div className="flex gap-1.5">
                    {onDownload && renderedUrl && (
                        <CircleActionButton title={t('VIDEO_EDITOR.DOWNLOAD')} icon={<Download />} onClick={onDownload} />
                    )}
                    {onRemove && (
                        <CircleActionButton title={t('VIDEO_EDITOR.NODE.REMOVE')} icon={<SvgRemove />} onClick={onRemove} destructive />
                    )}
                </div>
            )}
        >
            <div className="flex flex-col" data-ve-node={nodeId}>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); onOpenEditor(); }}
                    onMouseEnter={() => { void videoRef.current?.play().catch(() => undefined); }}
                    onMouseLeave={() => { if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; } }}
                    className="group relative w-full cursor-pointer overflow-hidden border-b bg-stone-900 bg-cover bg-center"
                    style={{ height: previewH, backgroundImage: poster ? `url(${poster})` : undefined }}
                >
                    {renderedUrl && (
                        <video
                            ref={videoRef}
                            src={renderedUrl}
                            poster={renderedThumbnailUrl}
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            className="absolute inset-0 size-full object-cover"
                        />
                    )}
                    {storyBusy && storyStills.length > 0 && (
                        <div className="absolute inset-0 grid grid-cols-2 gap-px bg-stone-900">
                            {storyStills.slice(0, 4).map((url, i) => (
                                <div key={`${url}-${i}`} className="bg-cover bg-center" style={{ backgroundImage: `url(${url})` }} />
                            ))}
                        </div>
                    )}
                    {!poster && !renderedUrl && !(storyBusy && storyStills.length > 0) && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-stone-300">
                            <Clapperboard className="size-6" strokeWidth={1.5} />
                            {story ? (story.title || t('VIDEO_EDITOR.STORY.NODE_LABEL')) : t('VIDEO_EDITOR.NODE.EMPTY')}
                        </div>
                    )}
                    {(poster || renderedUrl) && !running && (
                        <span className="absolute inset-0 flex items-center justify-center opacity-90 transition-opacity group-hover:opacity-0">
                            <span className="flex size-9 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm">
                                <Play className="size-4 translate-x-px" />
                            </span>
                        </span>
                    )}
                    <span className="absolute bottom-1.5 start-1.5 rounded bg-black/55 px-1.5 py-px text-[10px] tabular-nums text-white">
                        {t('VIDEO_EDITOR.NODE.CLIPS', { count: clips.length })}
                    </span>
                </div>

                {storyBusy && story ? (
                    <StoryStatus story={story} />
                ) : running ? (
                    <div className="flex flex-col gap-2 bg-background px-3 py-2.5">
                        <div className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                                <Loader2 className="size-3 animate-spin" />
                                {exportState?.progress?.phase === 'upload' ? t('VIDEO_EDITOR.PHASE.UPLOAD') : t('VIDEO_EDITOR.EXPORTING')}
                            </span>
                            <span className="tabular-nums text-foreground">{pct}%</span>
                        </div>
                        <Progress value={pct} />
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onCancelExport(); }}
                            className="flex items-center justify-center gap-1 self-end text-xs text-muted-foreground hover:text-destructive"
                        >
                            <X className="size-3" />
                            {t('VIDEO_EDITOR.CANCEL_EXPORT')}
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 divide-x border-t-0">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpenEditor(); }}
                            className="flex items-center justify-center gap-1.5 bg-background py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                            data-ve-node-open
                        >
                            <Clapperboard className="size-3.5" />
                            {t('VIDEO_EDITOR.NODE.OPEN')}
                        </button>
                        <button
                            type="button"
                            disabled={duration <= 0}
                            onClick={(e) => { e.stopPropagation(); onExport(); }}
                            className={cn(
                                'flex items-center justify-center gap-1.5 bg-background py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted',
                                'disabled:cursor-not-allowed disabled:opacity-50',
                            )}
                            data-ve-node-export
                        >
                            <Upload className="size-3.5" />
                            {t('VIDEO_EDITOR.EXPORT')}
                        </button>
                    </div>
                )}
                {exportState?.status === 'error' && (
                    <p className="border-t bg-background px-3 py-2 text-xs text-destructive">{exportState.error || t('VIDEO_EDITOR.EXPORT_FAILED')}</p>
                )}
            </div>
        </NodeShell>
    );
};

const StoryStatus: React.FC<{ story: NonNullable<VideoEditNodeProps['story']> }> = ({ story }) => {
    const { t } = useTranslation();
    const p = story.progress;
    const pct = p.total ? Math.round(((p.shot + p.animated) / (p.total * 2)) * 100) : 0;
    return (
        <div className="flex flex-col gap-2 bg-background px-3 py-2.5" data-vs-node-status={p.phase}>
            {story.title && <div className="truncate text-xs font-medium text-foreground">{story.title}</div>}
            {p.phase === 'failed' ? (
                <p className="text-xs text-destructive">{story.error || t('VIDEO_EDITOR.STORY.FAILED')}</p>
            ) : p.phase === 'writing' ? (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="size-3 animate-spin" />
                    {t('VIDEO_EDITOR.STORY.WRITING_SHORT')}
                </span>
            ) : (
                <>
                    <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Loader2 className="size-3 animate-spin" />
                            {p.phase === 'assembling'
                                ? t('VIDEO_EDITOR.STORY.ASSEMBLING')
                                : t('VIDEO_EDITOR.STORY.PROGRESS', { shot: p.shot, animated: p.animated, total: p.total })}
                        </span>
                        <span className="tabular-nums text-foreground">{pct}%</span>
                    </div>
                    <Progress value={pct} />
                    {p.failed > 0 && (
                        <p className="text-[11px] text-muted-foreground">{t('VIDEO_EDITOR.STORY.SOME_FAILED', { count: p.failed })}</p>
                    )}
                    {story.onAssembleNow && p.phase !== 'assembling' && (p.shot > 0) && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); story.onAssembleNow?.(); }}
                            className="self-end text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                        >
                            {t('VIDEO_EDITOR.STORY.ASSEMBLE_NOW')}
                        </button>
                    )}
                </>
            )}
        </div>
    );
};
